import {
  defaultMarkdownSerializer,
  MarkdownSerializerState,
  type MarkdownSerializer,
} from 'prosemirror-markdown';
import type { Mark, Node as PMNode } from '@tiptap/pm/model';
import type { JSONContent } from '@tiptap/core';
import { nexusSchema } from '@nexus/editor/schema';
import { stringify as stringifyYaml } from 'yaml';

type NodeSerializer = MarkdownSerializer['nodes'][string];
type MarkSpec = MarkdownSerializer['marks'][string];

export interface SerializeOptions {
  /**
   * Name to write for a linked page (its current title, so renames are followed).
   * Gets the target as written, e.g. to keep a link that uses an alias.
   */
  titleOf?: (pageId: string, target: string) => string | null | undefined;
  /** Indentation for nested list content. Matches the vault's dominant style by default. */
  indent?: string;
  frontmatter?: Record<string, unknown> | null;
}

/**
 * Escaping tuned for Obsidian notes: only escape what would otherwise change
 * meaning, so ordinary text like `snake_case`, `C:\Users` or `[2026]` stays readable.
 */
export function escapeText(str: string, startOfLine: boolean): string {
  let out = '';
  for (let i = 0; i < str.length; i++) {
    const c = str[i]!;
    const prev = i > 0 ? str[i - 1]! : '';
    const next = str[i + 1] ?? '';
    const rest = str.slice(i);
    let esc = false;
    switch (c) {
      case '\\':
        esc = /[!-/:-@[-`{-~]/.test(next) || next === '';
        break;
      case '`':
        esc = true;
        break;
      case '*':
        esc = !(/\s/.test(prev) && /\s/.test(next)) || (i === 0 && startOfLine);
        break;
      case '_':
        esc = !(/[\p{L}\p{N}]/u.test(prev) && /[\p{L}\p{N}]/u.test(next));
        break;
      case '~':
        esc = next === '~' || prev === '~';
        break;
      case '=':
        esc = next === '=';
        break;
      case '%':
        esc = next === '%';
        break;
      case '[':
        esc =
          next === '[' ||
          /^\[[^\]]*\]\s*[([:]/.test(rest) ||
          (i === 0 && startOfLine && /^\[.\]( |$)/.test(rest));
        break;
      case '!':
        esc = next === '[';
        break;
      case '<':
        esc = /[A-Za-z/!?]/.test(next);
        break;
      case '&':
        esc = /^&(#\d+|#x[\da-f]+|[a-z][a-z\d]*);/i.test(rest);
        break;
      case '#': {
        const body = /^[\p{L}\p{N}_\-/]+/u.exec(str.slice(i + 1))?.[0];
        esc =
          (prev === '' || /\s/.test(prev)) && !!body && /[^\d/]/u.test(body) && !body.endsWith('/');
        break;
      }
    }
    out += esc ? `\\${c}` : c;
  }
  if (startOfLine) {
    out = out
      .replace(/^([-+*])(?=[ \t]|$)/, '\\$1')
      .replace(/^>/, '\\>')
      .replace(/^(#{1,6})(?=[ \t]|$)/, '\\$1')
      .replace(/^(\d+)([.)])(?=[ \t]|$)/, '$1\\$2')
      .replace(/^([-=_]{3,})\s*$/, (m) => `\\${m}`)
      .replace(/^(`{3,}|~{3,})/, '\\$1')
      .replace(/^( {4,}|\t)/, (m) => m.replace(/ /g, '&#32;').replace(/\t/g, '&#9;'));
  }
  return out;
}

// The constructor, `out`, `inTightList` and `flushClose` are public at runtime but
// marked internal in the typings.
type StateWithOut = MarkdownSerializerState & {
  out: string;
  inAutolink?: boolean;
  inTightList: boolean;
  flushClose(size?: number): void;
};
const BaseState = MarkdownSerializerState as unknown as new (
  nodes: MarkdownSerializer['nodes'],
  marks: MarkdownSerializer['marks'],
  options: MarkdownSerializerState['options'],
) => StateWithOut;

class NexusState extends BaseState {
  override esc(str: string, startOfLine = false): string {
    // prosemirror-markdown only knows about block starts; after a hard break we are
    // also at the start of a line (a leading "- " there would start a list).
    const lineStart = startOfLine || /(^|\n)[ \t>]*$/.test(this.out);
    return escapeText(str, lineStart);
  }
}

/**
 * Item content in a tight list: blocks follow each other without blank lines
 * (a blank line would make the whole list loose on re-import).
 */
function renderItemContent(state: MarkdownSerializerState, node: PMNode) {
  const st = state as StateWithOut;
  let from = 0;
  // An item whose first line is empty but which has more content ("-\n    ![[x]]"):
  // put the content on the next line without a blank line — CommonMark would
  // otherwise end the item and turn the content into an indented code block.
  const first = node.firstChild;
  if (node.childCount > 1 && first?.type.name === 'paragraph' && first.childCount === 0) {
    st.write();
    st.out = `${st.out.replace(/[ \t]+$/, '')}\n`;
    from = 1;
  }
  node.forEach((child, _, i) => {
    if (i < from) return;
    if (i > from) st.flushClose(st.inTightList ? 1 : 2);
    state.render(child, node, i);
  });
}

/**
 * `<url>` only when re-import yields the same text: markdown-it decodes
 * percent-escapes in autolink text, so encoded URLs become `[url](url)`.
 */
function isAutolink(link: Mark, parent: PMNode, index: number): boolean {
  const href = link.attrs.href as string;
  if (link.attrs.title || !/^[a-z][a-z\d+.-]*:/i.test(href) || /%[\da-f]{2}|[\s<>]/i.test(href))
    return false;
  const content = parent.child(index);
  if (!content.isText || content.text !== href || content.marks[content.marks.length - 1] !== link)
    return false;
  return index === parent.childCount - 1 || !link.isInSet(parent.child(index + 1).marks);
}

const linkMark: MarkSpec = {
  open(state, mark, parent, index) {
    const st = state as StateWithOut;
    st.inAutolink = isAutolink(mark, parent, index);
    return st.inAutolink ? '<' : '[';
  },
  close(state, mark) {
    const st = state as StateWithOut;
    const auto = st.inAutolink;
    st.inAutolink = undefined;
    if (auto) return '>';
    const href = (mark.attrs.href as string).replace(/[()"]/g, '\\$&');
    const title = mark.attrs.title as string | null;
    return `](${href}${title ? ` "${title.replace(/"/g, '\\"')}"` : ''})`;
  },
};

function fenceFor(text: string): string {
  const longest = Math.max(0, ...(text.match(/`+/g) ?? []).map((r) => r.length));
  return '`'.repeat(Math.max(3, longest + 1));
}

function wikiText(
  attrs: { pageId: string | null; target: string; heading: string | null; alias?: string | null },
  titleOf: SerializeOptions['titleOf'],
): string {
  const title = (attrs.pageId && titleOf?.(attrs.pageId, attrs.target)) || attrs.target;
  return `${title}${attrs.heading ? `#${attrs.heading}` : ''}${attrs.alias ? `|${attrs.alias}` : ''}`;
}

/** `textAlign` worth writing (left is the default). */
const alignOf = (node: PMNode) => {
  const a = node.attrs.textAlign as string | null | undefined;
  return a && a !== 'left' ? a : null;
};

/** A colour safe to put in a style attribute. */
const cssValue = (mark: Mark) => String(mark.attrs.color).replace(/["<>;]/g, '');

function makeSerializer(opts: SerializeOptions) {
  const indent = opts.indent ?? '    ';
  const nodes: Record<string, NodeSerializer> = {
    paragraph(state, node) {
      const align = alignOf(node);
      if (align) state.write(`<p align="${align}">`);
      state.renderInline(node);
      if (align) state.write('</p>');
      state.closeBlock(node);
    },
    heading(state, node) {
      const align = alignOf(node);
      const level = node.attrs.level as number;
      state.write(align ? `<h${level} align="${align}">` : `${'#'.repeat(level)} `);
      state.renderInline(node, false);
      if (align) state.write(`</h${level}>`);
      state.closeBlock(node);
    },
    columns(state, node) {
      state.write('<!-- columns -->');
      state.closeBlock(node);
      node.forEach((column, _, i) => {
        if (i > 0) {
          state.write('<!-- column -->');
          state.closeBlock(node);
        }
        state.renderContent(column);
      });
      state.write('<!-- /columns -->');
      state.closeBlock(node);
    },
    blockquote(state, node) {
      state.wrapBlock('> ', null, node, () => state.renderContent(node));
    },
    callout(state, node) {
      const { kind, fold, title } = node.attrs as {
        kind: string;
        fold: string | null;
        title: string;
      };
      state.wrapBlock('> ', null, node, () => {
        state.write(`[!${kind}]${fold ?? ''}${title ? ` ${title}` : ''}`);
        const only = node.childCount === 1 ? node.firstChild : null;
        if (only?.type.name === 'paragraph' && only.childCount === 0) return;
        state.ensureNewLine();
        state.renderContent(node);
      });
    },
    codeBlock(state, node) {
      const fence = fenceFor(node.textContent);
      state.write(`${fence}${(node.attrs.language as string | null) ?? ''}\n`);
      // Always end with exactly one newline of our own, so trailing blank lines
      // inside the code survive the round trip.
      state.text(
        `${node.textContent}
`,
        false,
      );
      state.write(fence);
      state.closeBlock(node);
    },
    horizontalRule(state, node) {
      state.write('---');
      state.closeBlock(node);
    },
    bulletList(state, node) {
      const bullet = (node.attrs.bullet as string) || '-';
      state.renderList(node, indent, () => `${bullet} `);
    },
    taskList(state, node) {
      const bullet = (node.attrs.bullet as string) || '-';
      state.renderList(node, indent, () => `${bullet} `);
    },
    orderedList(state, node) {
      const start = (node.attrs.start as number) ?? 1;
      state.renderList(node, indent, (i) => `${start + i}. `);
    },
    listItem(state, node) {
      renderItemContent(state, node);
    },
    taskItem(state, node) {
      state.write(`[${(node.attrs.status as string) || (node.attrs.checked ? 'x' : ' ')}] `);
      renderItemContent(state, node);
    },
    hardBreak(state) {
      state.text('\n', false);
    },
    image: defaultMarkdownSerializer.nodes.image!,
    text(state, node) {
      state.text(node.text ?? '', !(state as StateWithOut).inAutolink);
    },
    wikiLink(state, node) {
      state.text(`[[${wikiText(node.attrs as never, opts.titleOf)}]]`, false);
    },
    embed(state, node) {
      const width = node.attrs.width as number | null;
      state.write(`![[${wikiText(node.attrs as never, opts.titleOf)}${width ? `|${width}` : ''}]]`);
      state.closeBlock(node);
    },
    hashtag(state, node) {
      state.text(`#${node.attrs.tag as string}`, false);
    },
    rawInline(state, node) {
      state.text(node.attrs.text as string, false);
    },
    rawBlock(state, node) {
      state.text(node.attrs.text as string, false);
      state.closeBlock(node);
    },
    table(state, node) {
      const rows: string[][] = [];
      node.forEach((row) => {
        const cells: string[] = [];
        row.forEach((cell) => {
          const parts: string[] = [];
          cell.forEach((block) => {
            const s = new NexusState(nodes, marks, { hardBreakNodeName: 'hardBreak' });
            s.renderInline(block);
            parts.push(s.out.replace(/\n/g, '<br>'));
          });
          cells.push(parts.join('<br>').replace(/(?<!\\)\|/g, '\\|'));
        });
        rows.push(cells);
      });
      const width = Math.max(...rows.map((r) => r.length));
      const line = (cells: string[]) =>
        `| ${Array.from({ length: width }, (_, i) => cells[i] ?? '').join(' | ')} |`;
      rows.forEach((cells, i) => {
        state.write(line(cells));
        state.ensureNewLine();
        if (i === 0) {
          state.write(`| ${Array.from({ length: width }, () => '---').join(' | ')} |`);
          state.ensureNewLine();
        }
      });
      state.closeBlock(node);
    },
  };
  const marks: Record<string, MarkSpec> = {
    italic: { open: '*', close: '*', mixable: true, expelEnclosingWhitespace: true },
    bold: { open: '**', close: '**', mixable: true, expelEnclosingWhitespace: true },
    strike: { open: '~~', close: '~~', mixable: true, expelEnclosingWhitespace: true },
    highlight: {
      open: (_s, mark) =>
        mark.attrs.color ? `<mark style="background: ${cssValue(mark)}">` : '==',
      close: (_s, mark) => (mark.attrs.color ? '</mark>' : '=='),
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    underline: { open: '<u>', close: '</u>', mixable: true, expelEnclosingWhitespace: true },
    textStyle: {
      open: (_s, mark) => (mark.attrs.color ? `<span style="color: ${cssValue(mark)}">` : ''),
      close: (_s, mark) => (mark.attrs.color ? '</span>' : ''),
      mixable: true,
      expelEnclosingWhitespace: true,
    },
    code: defaultMarkdownSerializer.marks.code!,
    link: linkMark,
  };
  return { nodes, marks };
}

export function serializeFrontmatter(data: Record<string, unknown>): string {
  if (Object.keys(data).length === 0) return '';
  return `---\n${stringifyYaml(data, { lineWidth: 0 }).trimEnd()}\n---\n`;
}

/** Serialize a page document (+ optional front matter) to Obsidian-compatible Markdown. */
export function serializeMarkdown(doc: JSONContent | PMNode, opts: SerializeOptions = {}): string {
  const node = 'toJSON' in doc ? (doc as PMNode) : nexusSchema().nodeFromJSON(doc);
  const { nodes, marks } = makeSerializer(opts);
  const state = new NexusState(nodes, marks, { hardBreakNodeName: 'hardBreak', strict: true });
  state.renderContent(node);
  const body = state.out.replace(/\s+$/, '');
  const fm = opts.frontmatter ? serializeFrontmatter(opts.frontmatter) : '';
  return `${fm}${fm && body ? '\n' : ''}${body}${body ? '\n' : ''}`;
}
